import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { ProviderType } from '#/models/ProviderDetails';
import { useTranslation } from 'react-i18next';

import { SnackCardProvider } from '../../hooks/useSnackCardProviderSearch/useSnackCardProviderSearch';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { CardInfo } from './CardInfo';
import {
  SnackCardWithExternalLinkIcon,
  SnackCardWithExternalLinkText,
  SnackCardWithExternalLinkType,
  SnackCardsWithExternalLink,
  SnackContainer,
  SnackInfoCard,
} from './SnackCard.styles';

type Props = {
  displayName: string;
  providerSpeciality: string;
  providerType: ProviderType;
  isFixes: boolean;
  css?: any;
  handleClick: any;
  headers: ResponseHeaders;
  indexForAnalytics: number;
  isSmartChoiceFFEnabled: boolean;
  locationForAnalytics: string;
  practitioner: SnackCardProvider;
};

export const SnackCardWithExternalLink = ({
  displayName,
  providerSpeciality,
  providerType,
  isFixes,
  css,
  headers,
  indexForAnalytics,
  isSmartChoiceFFEnabled,
  locationForAnalytics,
  practitioner = {},
  handleClick,
}: Props & { headers: any }) => {
  const { t } = useTranslation();
  const isGEHAV2 = shouldUseGehaV2Styling();

  return (
    <SnackContainer
      data-auto-testid="snack-card-with-link-container"
      data-testid="snack-card-with-link-container"
      onClick={handleClick}
    >
      <SnackInfoCard
        alignContent="center"
        alignItems="center"
        css={css}
        data-auto-testid={`snack-card-${providerType}`.replace(/ /g, '-')}
        data-testid={`snack-card-${providerType}`.replace(/ /g, '-')}
        direction="column"
        isFixes={isFixes}
        isSnackCardWithExternalLink
        justify="space-between"
      >
        <Flex
          css={{
            'abyss-flex-root': {
              alignSelf: 'stretch',
            },
          }}
          data-testid="providers-container"
          direction="column"
        >
          <SnackCardWithExternalLinkIcon>
            <IconSymbol
              color="$core.color.brand.60"
              data-testid="providers-speciality-link"
              icon="open_in_new"
              size="18px"
            />
          </SnackCardWithExternalLinkIcon>
          <SnackCardsWithExternalLink>
            <Tooltip content={displayName}>
              <SnackCardWithExternalLinkText
                aria-label={`${displayName}, ${providerSpeciality}. ${t(
                  'OPENS_NEW_WINDOW'
                )}`}
                data-testid="providers-speciality"
                variant="outline"
              >
                {displayName}
              </SnackCardWithExternalLinkText>
            </Tooltip>
          </SnackCardsWithExternalLink>
          <Tooltip content={providerSpeciality}>
            <SnackCardWithExternalLinkType cssProps={{ isGEHAV2 }}>
              {providerSpeciality}
            </SnackCardWithExternalLinkType>
          </Tooltip>
          {isSmartChoiceFFEnabled && (
            <Flex
              css={{ marginTop: '8px' }}
              data-testid="snack-card-external-link"
              justify="center"
              onClick={(e) => e.stopPropagation()}
            >
              <CardInfo
                cardType="snack-card-internal"
                headers={headers}
                indexForAnalytics={indexForAnalytics}
                isSmartChoiceFFEnabled={isSmartChoiceFFEnabled}
                landingPage
                locationForAnalytics={`body:${locationForAnalytics}`}
                practitioner={practitioner}
              />
            </Flex>
          )}
        </Flex>
      </SnackInfoCard>
    </SnackContainer>
  );
};
